import { useEffect, useState } from "react";
import "@/App.css";
import axios from "axios";
import ReactGA from "react-ga4";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

// Initialize Google Analytics
if (GA_MEASUREMENT_ID) {
  ReactGA.initialize(GA_MEASUREMENT_ID, {
    gaOptions: {
      send_page_view: true
    }
  });
  // Send initial page view
  ReactGA.send({ hitType: "pageview", page: window.location.pathname });
}

function App() {
  const [astronautCount, setAstronautCount] = useState(null);
  const [astronautList, setAstronautList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedAstronaut, setSelectedAstronaut] = useState(null);
  const [showBioModal, setShowBioModal] = useState(false);
  const [showChangelogModal, setShowChangelogModal] = useState(false);

  const fetchAstronauts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/astronauts`);
      if (response.data.success) {
        setAstronautCount(response.data.number);
        setAstronautList(response.data.people);
        setError(null);
        
        // Track successful data fetch event
        if (GA_MEASUREMENT_ID) {
          ReactGA.event({
            category: "Data",
            action: "Astronaut Data Fetched",
            label: `Count: ${response.data.number}`,
            value: response.data.number
          });
        }
      } else {
        setError("Veri alınamadı");
      }
    } catch (e) {
      console.error("Error fetching astronaut data:", e);
      setError("Bağlantı hatası");
      
      // Track error event
      if (GA_MEASUREMENT_ID) {
        ReactGA.event({
          category: "Error",
          action: "Data Fetch Failed",
          label: e.message
        });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAstronauts();
    // Her 30 saniyede bir güncelle
    const interval = setInterval(fetchAstronauts, 30000);
    return () => clearInterval(interval);
  }, []);

  // ESC tuşu ve dışarı tıklama için changelog'u kapat
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        setShowChangelogModal(false);
      }
    };

    const handleClickOutside = (e) => {
      if (showChangelogModal && !e.target.closest('.changelog-popup') && !e.target.closest('.version-link')) {
        setShowChangelogModal(false);
      }
    };

    if (showChangelogModal) {
      document.addEventListener('keydown', handleEsc);
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showChangelogModal]);

  const handleCountClick = () => {
    setShowModal(true);
    
    // Track modal open event
    if (GA_MEASUREMENT_ID) {
      ReactGA.event({
        category: "Engagement",
        action: "Astronaut List Opened",
        label: "Modal Opened",
        value: astronautCount
      });
    }
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const openBioModal = (astronaut) => {
    setSelectedAstronaut(astronaut);
    setShowBioModal(true);
    
    // Track bio view
    if (GA_MEASUREMENT_ID) {
      ReactGA.event({
        category: "Engagement",
        action: "Astronaut Bio Viewed",
        label: astronaut.name
      });
    }
  };

  const closeBioModal = () => {
    setShowBioModal(false);
    setSelectedAstronaut(null);
  };

  return (
    <div className="space-app" data-testid="space-app">
      <div className="space-overlay"></div>
      <div className="content" data-testid="content-container">
        {loading && astronautCount === null ? (
          <div className="loading" data-testid="loading-indicator">
            <div className="spinner"></div>
            <p>Yükleniyor...</p>
          </div>
        ) : error ? (
          <div className="error" data-testid="error-message">
            <p>{error}</p>
          </div>
        ) : (
          <>
            <div 
              className="astronaut-count" 
              data-testid="astronaut-count"
              onClick={handleCountClick}
              style={{ cursor: "pointer" }}
              title="Listeyi görmek için tıklayın"
            >
              {astronautCount}
            </div>
            <div className="description" data-testid="description-text">
              kişi şu anda uzayda
            </div>
            <div className="subtext" data-testid="subtext">
              Listeyi görmek için sayıya tıklayın
            </div>
          </>
        )}
      </div>

      {/* Astronaut List Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal} data-testid="modal-overlay">
          <div className="modal-content" onClick={(e) => e.stopPropagation()} data-testid="modal-content">
            <div className="modal-header">
              <h2>Uzaydaki Astronotlar</h2>
              <button className="close-button" onClick={closeModal} data-testid="close-modal">
                ✕
              </button>
            </div>
            <div className="modal-body">
              {/* Group astronauts by station */}
              {Object.entries(
                astronautList.reduce((groups, astronaut) => {
                  const station = astronaut.craft || 'Unknown Station';
                  if (!groups[station]) {
                    groups[station] = [];
                  }
                  groups[station].push(astronaut);
                  return groups;
                }, {})
              ).map(([stationName, stationAstronauts], groupIndex) => (
                <div key={groupIndex} className="station-group" data-testid={`station-group-${groupIndex}`}>
                  <div className="station-header">
                    <h3>{stationName}</h3>
                    <span className="station-count">{stationAstronauts.length} kişi</span>
                  </div>
                  <div className="astronaut-list">
                    {stationAstronauts.map((astronaut, index) => (
                      <div 
                        key={index} 
                        className="astronaut-item clickable-card" 
                        data-testid={`astronaut-${groupIndex}-${index}`}
                        onClick={() => openBioModal(astronaut)}
                        title="Detaylar için tıklayın"
                      >
                        <div className="astronaut-photo-container">
                          {astronaut.photo ? (
                            <img 
                              src={astronaut.photo} 
                              alt={astronaut.name}
                              className="astronaut-photo"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div className="astronaut-photo-fallback" style={{ display: astronaut.photo ? 'none' : 'flex' }}>
                            👨‍🚀
                          </div>
                        </div>
                        <div className="astronaut-info">
                          <div className="astronaut-country-row">
                            <span className="astronaut-flag">{astronaut.flag}</span>
                            <span className="astronaut-country">{astronaut.country}</span>
                          </div>
                          <div className="astronaut-name-row">
                            <span className="astronaut-name">{astronaut.name}</span>
                            <span className="astronaut-separator">•</span>
                            <span className="astronaut-mission">{astronaut.mission}</span>
                          </div>
                        </div>
                        <div className="astronaut-days">
                          <div className="days-number">{astronaut.days_in_space}</div>
                          <div className="days-label">gün</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Biography Modal */}
      {showBioModal && selectedAstronaut && (
        <div className="modal-overlay bio-modal" onClick={closeBioModal} data-testid="bio-modal-overlay">
          <div className="bio-modal-content" onClick={(e) => e.stopPropagation()} data-testid="bio-modal-content">
            <button className="close-button" onClick={closeBioModal} data-testid="close-bio-modal">
              ✕
            </button>
            <div className="bio-header">
              <div className="bio-photo-large">
                {selectedAstronaut.photo ? (
                  <img 
                    src={selectedAstronaut.photo} 
                    alt={selectedAstronaut.name}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div className="bio-photo-fallback" style={{ display: selectedAstronaut.photo ? 'none' : 'flex' }}>
                  👨‍🚀
                </div>
              </div>
              <div className="bio-title">
                <h2>{selectedAstronaut.name}</h2>
                <div className="bio-country">
                  <span className="bio-flag">{selectedAstronaut.flag}</span>
                  <span>{selectedAstronaut.country}</span>
                </div>
              </div>
            </div>
            <div className="bio-stats">
              <div className="bio-stat">
                <div className="stat-label">Görev</div>
                <div className="stat-value">{selectedAstronaut.mission}</div>
              </div>
              <div className="bio-stat">
                <div className="stat-label">İstasyon</div>
                <div className="stat-value">{selectedAstronaut.craft}</div>
              </div>
              <div className="bio-stat">
                <div className="stat-label">Uzayda</div>
                <div className="stat-value">{selectedAstronaut.days_in_space} gün</div>
              </div>
            </div>
            <div className="bio-content">
              <h3>Biyografi</h3>
              <p>{selectedAstronaut.bio || "Biyografi bilgisi mevcut değil."}</p>
            </div>
          </div>
        </div>
      )}

      {/* Changelog Modal */}
      {showChangelogModal && (
        <div className="changelog-popup" onClick={(e) => e.stopPropagation()}>
          <button className="close-button" onClick={() => setShowChangelogModal(false)}>×</button>
          <h2>Changelog</h2>
          <div className="changelog-content">
            <div className="changelog-version">
              <h3>v0.0.3 <span className="changelog-date">(15 Ocak 2026)</span></h3>
              <ul>
                <li>Astronot fotoğrafları eklendi.</li>
                <li>Liste satırlarına tıklandığında astronotlar hakkında kısa bilgi modalı eklendi.</li>
                <li>Sefer/görev adı eklendi.</li>
                <li>Küçük görsel düzenlemeler yapıldı.</li>
              </ul>
            </div>
            <div className="changelog-version">
              <h3>v0.0.2 <span className="changelog-date">(24 Ekim 2025)</span></h3>
              <ul>
                <li>Astronot sayısına tıklandığında hangi astronotun hangi istasyonda olduğunu gösteren liste eklendi.</li>
                <li>Astronotların milliyetleri ve uzayda bulunma süreleri eklendi.</li>
                <li>Google Analytics entegrasyonu yapıldı.</li>
              </ul>
            </div>
            <div className="changelog-version">
              <h3>v0.0.1 <span className="changelog-date">(23 Ekim 2025)</span></h3>
              <ul>
                <li>MVP tamamlandı, uzaydaki astronot sayısı ekranda tek bir sayı olarak gösterildi.</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Version Footer */}
      <div className="version-footer">
        <button 
          className="version-link"
          onClick={() => setShowChangelogModal(true)}
        >
          v0.0.3
        </button>
        <span className="copyright">© {new Date().getFullYear()}</span>
      </div>
    </div>
  );
}

export default App;